import React, { useEffect, useMemo, useState } from "react";
import {
  Target, TrendingUp, Lightbulb, AlertTriangle, Clock, DollarSign, Repeat, Users,
  LineChart, ClipboardList, ChevronDown, Search, Building2, HelpCircle,
} from "lucide-react";
import { apiGet, unpackList } from "../../Api/Client.js";
import { formatMoney } from "../../Shared/money.js";
import Scorecard from "./SellerRadar/Scorecard.jsx";
import GrowthPanel from "./SellerRadar/GrowthPanel.jsx";
import PatternsPanel from "./SellerRadar/PatternsPanel.jsx";
import SkillProgress from "./SellerRadar/SkillProgress.jsx";
import TeamHeatmap from "./SellerRadar/TeamHeatmap.jsx";
import ReviewQueue from "./SellerRadar/ReviewQueue.jsx";
import DealPanel from "./SellerRadar/DealPanel.jsx";
import AccountsPanel from "./SellerRadar/AccountsPanel.jsx";
import HowScoringWorks from "./SellerRadar/HowScoringWorks.jsx";

// RevOps — goal 3 of the sales OS, Bassi's own three-part definition:
//   (a) deal intelligence    — how a deal is progressing
//   (b) hacks in progression — buyer-psychology + playbook hacks
//   (c) seller psychology    — the scorecard, per person, per level
//
// TWO PEOPLE OPEN THIS PAGE AND THEY ARE NOT ASKING THE SAME THING.
//
//   a rep      "how am I doing, and what do I say differently tomorrow?"
//   a manager  "who needs me this week, and is this one person or the playbook?"
//
// For a long time both got the same screen, and it was built around the rep's
// question with the manager's bolted on: the person picker was assembled out
// of whoever happened to have scored calls, which meant a rep with nothing
// scored yet did not exist on this page at all — exactly backwards, since a new
// SDR or one who has gone quiet for a fortnight is the person a manager most
// needs to open. And a manager landing on somebody's scorecard has already
// skipped the question they actually came with.
//
// So the screen now branches once, at the top, on whether this is somebody's
// own record or somebody's team:
//
//   MANAGER   opens on the review queue — the ritual, not a report. The person
//             picker comes from the LADDER (/RevOps/roster/), so everybody on a
//             track is selectable whether or not they have calls, filtered by
//             SDR / AE, and the choice carries across every tab.
//
//   REP       no picker, no roster fetch, no team tab. There is nothing to pick:
//             every endpoint scopes its rows to the caller, so their list has
//             one entry and a dropdown would be a control that cannot do
//             anything.
//
// The branch decides what to OFFER. It is not access control — every endpoint
// behind these tabs enforces its own scoping server-side, and the two that show
// everybody's numbers (team patterns, review queue) return 403 to a rep
// regardless of what this file renders. Hiding a control is not a permission,
// which is exactly how the equivalent hole was found on the onboarding rows.

const card = { background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 14, padding: "16px 18px" };
const muted = { color: "var(--c-500)" };

const MANAGER_TABS = [
  { key: "queue", label: "This week", icon: ClipboardList },
  { key: "scorecard", label: "Scorecard", icon: Target },
  { key: "growth", label: "Growth", icon: LineChart },
  { key: "patterns", label: "Patterns", icon: Repeat },
  { key: "accounts", label: "Accounts", icon: Building2 },
  { key: "team", label: "Team", icon: Users },
  { key: "deals", label: "Deal Intelligence", icon: TrendingUp },
  { key: "signals", label: "Deal Signals", icon: AlertTriangle },
  { key: "hacks", label: "Hacks", icon: Lightbulb },
  { key: "how", label: "How scoring works", icon: HelpCircle },
];

// A rep's view, on purpose narrower than what the page can show. "My deals"
// (deal signals) and "Hacks" are still reachable server-side for a rep on
// their own rows — DealSignalsView self-scopes deliberately, "an AE owns
// their own deals" — this just doesn't tab to them; a manager reaches the
// full set via MANAGER_TABS. Narrowed to three 2026-09-17: the scorecard,
// the trend, and the pattern behind it is the complete "how am I doing"
// answer a rep opens this page for.
const REP_TABS = [
  { key: "scorecard", label: "My scorecard", icon: Target },
  { key: "growth", label: "My growth", icon: LineChart },
  { key: "patterns", label: "My patterns", icon: Repeat },
  // The companies they are working, with a brief for the next call. Built
  // from their own calls only, like everything else a rep sees here.
  { key: "accounts", label: "My accounts", icon: Building2 },
  // How a call becomes a score, in plain words. A score nobody can follow is
  // a score nobody trusts, and the rep is the one being scored.
  { key: "how", label: "How scoring works", icon: HelpCircle },
];

function DealRow({ deal, tag }) {
  const name = deal.company_name || deal.full_name || `Lead #${deal.id}`;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "7px 0", borderBottom: "1px solid var(--c-200)", fontSize: 12.5 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ color: "var(--c-900)", fontWeight: 600 }}>{name}</div>
        <div style={muted}>{deal.stage} · {deal.owner__display_name || "unassigned"}</div>
      </div>
      <div style={{ textAlign: "right", ...muted, flexShrink: 0 }}>{tag}</div>
    </div>
  );
}

function DealsTab({ company }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet("/RevOps/deal-intelligence/")
      .then((payload) => !cancelled && setData(payload))
      .catch((err) => !cancelled && setError(err?.message || "Failed to load deal intelligence."));
    return () => { cancelled = true; };
  }, []);

  if (error) return <div className="Login-Error">{error}</div>;
  if (!data) return <p style={muted}>Loading…</p>;

  const g = data.at_a_glance || {};
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
        {[
          { label: "Open deals", value: g.open_deals, icon: TrendingUp },
          { label: "Overdue follow-ups", value: g.overdue_follow_ups, icon: Clock },
          { label: "Stalled deals", value: g.stalled_deals, icon: AlertTriangle },
          { label: "Pipeline value", value: g.pipeline_value ? formatMoney(g.pipeline_value, company) : "—", icon: DollarSign },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} style={{ ...card, textAlign: "center" }}>
            <Icon size={16} color="var(--blue-600)" />
            <div style={{ fontSize: 20, fontWeight: 700, color: "var(--c-900)", marginTop: 4 }}>{value ?? "—"}</div>
            <div style={{ fontSize: 11, ...muted }}>{label}</div>
          </div>
        ))}
      </div>

      <div style={card}>
        <strong style={{ fontSize: 13, color: "var(--c-900)" }}>Overdue follow-ups</strong>
        <div style={{ marginTop: 6 }}>
          {(data.overdue_follow_ups || []).length === 0 && <p style={{ ...muted, fontSize: 12.5 }}>None — clean.</p>}
          {(data.overdue_follow_ups || []).map((d) => <DealRow key={d.id} deal={d} tag={`${d.days_overdue}d overdue`} />)}
        </div>
      </div>

      <div style={card}>
        <strong style={{ fontSize: 13, color: "var(--c-900)" }}>Stalled — no activity in {data.stall_window_days} days</strong>
        <div style={{ marginTop: 6 }}>
          {(data.stalled_deals || []).length === 0 && (data.never_touched_deals || []).length === 0 && (
            <p style={{ ...muted, fontSize: 12.5 }}>None — clean.</p>
          )}
          {(data.never_touched_deals || []).map((d) => <DealRow key={d.id} deal={d} tag="never touched" />)}
          {(data.stalled_deals || []).map((d) => <DealRow key={d.id} deal={d} tag="stalled" />)}
        </div>
      </div>

      <div style={card}>
        <strong style={{ fontSize: 13, color: "var(--c-900)" }}>Pipeline by stage</strong>
        <div style={{ marginTop: 6 }}>
          {(data.pipeline_by_stage || []).map((s) => (
            <div key={s.stage} style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, padding: "5px 0", borderBottom: "1px solid var(--c-200)" }}>
              <span>{s.stage}</span>
              <span style={muted}>{s.count} deal{s.count === 1 ? "" : "s"} · {formatMoney(s.value || 0, company)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HacksTab() {
  const [businesses, setBusinesses] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiGet("/Playbook/Businesses/")
      .then((payload) => !cancelled && setBusinesses(unpackList(payload)))
      .catch((err) => !cancelled && setError(err?.message || "Failed to load hacks."));
    return () => { cancelled = true; };
  }, []);

  const hacks = useMemo(() => {
    const rows = [];
    (businesses || []).forEach((biz) => {
      (biz.stages || []).forEach((stage) => {
        (stage.substeps || []).forEach((step) => {
          if (step.psychology) rows.push({ kind: "Buyer psychology", stage: stage.name, biz: biz.name, text: step.psychology, key: `${step.id}-psy` });
          if (step.best_practice) rows.push({ kind: "Playbook hack", stage: stage.name, biz: biz.name, text: step.best_practice, key: `${step.id}-bp` });
        });
      });
    });
    return rows;
  }, [businesses]);

  if (error) return <div className="Login-Error">{error}</div>;
  if (!businesses) return <p style={muted}>Loading…</p>;
  if (!hacks.length) return <p style={muted}>No hacks recorded yet — they come from the same Playbook substeps (psychology + best-practice fields). Add substeps in Django admin.</p>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {hacks.map((h) => (
        <div key={h.key} style={card}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em", padding: "2px 8px", borderRadius: 999, background: h.kind === "Buyer psychology" ? "var(--blue-50, rgba(80,120,255,.12))" : "var(--green-50, rgba(40,180,120,.14))", color: h.kind === "Buyer psychology" ? "var(--blue-600)" : "var(--green-600)" }}>
              {h.kind}
            </span>
            <span style={{ fontSize: 11, ...muted }}>{h.biz} · {h.stage}</span>
          </div>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: "var(--c-800)" }}>{h.text}</p>
        </div>
      ))}
    </div>
  );
}

// ── The manager's "whose numbers am I looking at" bar ────────────────────────
//
// Present on every per-person tab, because a manager asks "how is this person
// doing" once, not once per tab. It carries three things a bare dropdown does
// not: the SDR/AE filter (two different rubrics, two different jobs, and a
// manager is usually thinking about one group at a time), the call count so a
// thin record is visible before the radar is read, and a search box, because a
// select with thirty names in it is a list you scroll rather than a control you
// use.

function ManagerBar({ roster, employeeId, onSelect, trackFilter, onTrackFilter }) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return roster.filter((p) => !q || (p.name || "").toLowerCase().includes(q));
  }, [roster, query]);

  const selected = roster.find((p) => String(p.employee) === String(employeeId));
  const counts = useMemo(() => ({
    sdr: roster.filter((p) => p.track === "junior").length,
    ae: roster.filter((p) => p.track === "senior").length,
  }), [roster]);

  return (
    <div style={{ ...card, display: "flex", flexWrap: "wrap", gap: 14, alignItems: "center",
                  justifyContent: "space-between", marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
        <span style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".05em" }}>
          Viewing
        </span>
        <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <select value={employeeId || ""} onChange={(e) => onSelect(e.target.value)}
                  style={{ appearance: "none", padding: "7px 30px 7px 11px", fontSize: 14.5,
                           fontWeight: 700, borderRadius: 9, border: "1px solid var(--c-200)",
                           background: "var(--surface)", color: "var(--c-900)", cursor: "pointer",
                           maxWidth: 300 }}>
            {visible.length === 0 && <option value="">No one matches “{query}”</option>}
            {visible.map((p) => (
              <option key={p.employee} value={p.employee}>
                {p.name} — {p.role}
                {p.total_calls ? ` · ${p.total_calls} call${p.total_calls === 1 ? "" : "s"}` : " · no calls yet"}
              </option>
            ))}
          </select>
          <ChevronDown size={14} style={{ position: "absolute", right: 10, pointerEvents: "none",
                                          color: "var(--c-500)" }} />
        </span>

        {selected && (
          <span style={{ fontSize: 10.5, ...muted, textTransform: "uppercase", letterSpacing: ".05em",
                         padding: "3px 8px", borderRadius: 999,
                         background: "var(--c-100, rgba(120,120,120,.12))" }}
                title={selected.employee_code || undefined}>
            {selected.role}
          </span>
        )}
        {/* Where they actually sit on the ladder — the roster is built from this
            row precisely so it can say it, and it was being fetched and thrown
            away before reaching this bar. */}
        {selected?.stage_label && (
          <span style={{ fontSize: 11, color: "var(--blue-600)" }}>
            {selected.stage_label}
          </span>
        )}
        {/* Said out loud rather than left as an empty radar. A rep with nothing
            scored is a normal state a manager needs to be able to look at, and
            the reason the page is blank should be on the page. */}
        {selected && !selected.total_calls && (
          <span style={{ fontSize: 11.5, color: "var(--red-600, #c0392b)" }}>
            nothing scored yet
          </span>
        )}
        {/* The breakdown behind `total_calls` — cold calls vs demos, and how
            many were scored from the recording rather than entered by hand.
            The roster computed all three and only the sum was reaching this
            bar. */}
        {selected && selected.total_calls > 0 && (
          <span style={{ fontSize: 11, ...muted }}>
            {selected.calls_count || 0} call{selected.calls_count === 1 ? "" : "s"} ·{" "}
            {selected.meetings_count || 0} meeting{selected.meetings_count === 1 ? "" : "s"} ·{" "}
            {selected.auto_scored_count || 0} auto-scored
            {selected.last_call_date
              ? ` · last on ${new Date(`${selected.last_call_date}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`
              : ""}
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
          <Search size={12} style={{ position: "absolute", left: 9, color: "var(--c-500)",
                                     pointerEvents: "none" }} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a rep"
                 style={{ padding: "6px 10px 6px 26px", fontSize: 12, borderRadius: 8,
                          border: "1px solid var(--c-200)", background: "var(--surface)",
                          color: "var(--c-900)", width: 130 }} />
        </span>
        <div style={{ display: "flex", gap: 3 }}>
          {[["", `All ${roster.length}`], ["junior", `SDR ${counts.sdr}`], ["senior", `AE ${counts.ae}`]]
            .map(([key, label]) => (
              <button key={key || "all"} onClick={() => onTrackFilter(key)} style={{
                border: "1px solid var(--c-200)",
                background: trackFilter === key ? "var(--c-100, rgba(120,120,120,.14))" : "transparent",
                cursor: "pointer", fontSize: 11.5, padding: "5px 10px", borderRadius: 7,
                fontWeight: trackFilter === key ? 700 : 400,
                color: trackFilter === key ? "var(--c-900)" : "var(--c-500)",
              }}>{label}</button>
            ))}
        </div>
      </div>
    </div>
  );
}

export default function RevOpsScreen({ data: appData }) {
  const [me, setMe] = useState(null);
  const [rubric, setRubric] = useState(null);
  const [calls, setCalls] = useState(null);
  const [roster, setRoster] = useState(null);
  const [tab, setTab] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [trackFilter, setTrackFilter] = useState("");
  // {id, at, nonce}: a call (and moment) another tab asked the scorecard to
  // open. Cleared once the scorecard has acted on it, so coming back to the
  // tab later does not snap back to it.
  const [focusCall, setFocusCall] = useState(null);
  // A company another tab asked the Accounts tab to open (the call card's
  // "Prep for the next call").
  const [focusCompany, setFocusCompany] = useState(null);
  const openAccount = (company) => {
    setFocusCompany(company);
    setTab("accounts");
  };
  const openCall = (id, at = null) => {
    setFocusCall({ id, at, nonce: Date.now() });
    setTab("scorecard");
  };

  const isManager = Boolean(me?.isSuperuser);

  useEffect(() => {
    let cancelled = false;
    apiGet("/RevOps/rubric/").then((data) => !cancelled && setRubric(data)).catch(() => {});
    // Who is looking. Only used to decide what to OFFER — every one of these
    // endpoints enforces its own scoping server-side, so this cannot grant
    // anything the API would refuse.
    apiGet("/Users/Auth/Me/").then((data) => !cancelled && setMe(data?.user || null))
      .catch(() => !cancelled && setMe({}));
    // Fetched once and handed down, so switching tabs does not refetch the
    // whole scored history each time.
    apiGet("/RevOps/SellerCallScores/")
      .then((payload) => !cancelled && setCalls(unpackList(payload)))
      .catch(() => !cancelled && setCalls([]));
    return () => { cancelled = true; };
  }, []);

  // After a coach adjusts a score the radar, history and averages must move
  // with it, so the lean list is fetched again.
  const refreshCalls = () => {
    apiGet("/RevOps/SellerCallScores/").then((payload) => setCalls(unpackList(payload))).catch(() => {});
  };

  // The roster is the manager's list of people and comes from the LADDER, not
  // from the calls — so somebody with nothing scored is still selectable. A rep
  // never fetches it: it is superuser-only and would be a 403 with a spinner.
  useEffect(() => {
    if (!isManager) { setRoster([]); return; }
    let cancelled = false;
    apiGet("/RevOps/roster/")
      .then((payload) => !cancelled && setRoster(payload?.people || []))
      .catch(() => !cancelled && setRoster([]));
    return () => { cancelled = true; };
  }, [isManager]);

  // Land a manager on the question they came with, and a rep on their own card.
  useEffect(() => {
    if (me && tab == null) setTab(isManager ? "queue" : "scorecard");
  }, [me, isManager, tab]);

  // One list of people for every per-person panel, in the shape they already
  // read. For a manager it is the roster with each person's calls attached; for
  // a rep it is derived from their own calls, which is the only record the API
  // will hand them anyway.
  const people = useMemo(() => {
    const byEmployee = {};
    (calls || []).forEach((c) => {
      (byEmployee[c.employee] = byEmployee[c.employee] || []).push(c);
    });

    if (isManager && roster) {
      const listed = (roster || [])
        .filter((p) => !trackFilter || p.track === trackFilter)
        .map((p) => ({
          employee: p.employee_id,
          name: p.name,
          track: p.track,
          role: p.role,
          employee_code: p.employee_code,
          // Named `*_count` here, distinct from `calls` below, which is this
          // person's actual scored-call objects — the roster's own `calls`
          // field is a count of cold-call-modality rows and would silently
          // shadow it under the same name.
          stage: p.stage,
          stage_label: p.stage_label,
          calls_count: p.calls,
          meetings_count: p.meetings,
          auto_scored_count: p.auto_scored,
          total_calls: p.total_calls,
          last_call_date: p.last_call_date,
          calls: byEmployee[p.employee_id] || [],
        }));
      // Anybody with scored calls who is not on the ladder. Should not happen,
      // and if it does the calls exist and must not vanish from the one screen
      // that would show somebody the mistake.
      const known = new Set(listed.map((p) => String(p.employee)));
      Object.entries(byEmployee).forEach(([id, rows]) => {
        if (!known.has(String(id)) && !trackFilter) {
          listed.push({
            employee: Number(id), name: rows[0].employee_name, track: rows[0].track,
            role: rows[0].track === "senior" ? "AE" : "SDR",
            total_calls: rows.length, calls: rows,
          });
        }
      });
      return listed;
    }

    return Object.entries(byEmployee).map(([id, rows]) => ({
      employee: Number(id), name: rows[0].employee_name, track: rows[0].track,
      role: rows[0].track === "senior" ? "AE" : "SDR",
      total_calls: rows.length, calls: rows,
    }));
  }, [calls, roster, isManager, trackFilter]);

  // Keep a valid selection as the filter changes, without stamping over one the
  // manager made deliberately.
  //
  // Deliberately waits for the roster before choosing anybody. `calls` almost
  // always resolves first, and picking from it lands the manager on whichever
  // rep the API happened to return first — which is arbitrary, and looked like
  // a default nobody chose. The roster is ordered most-recently-active first,
  // so waiting one fetch is the difference between a default and a coin toss.
  useEffect(() => {
    if (isManager && !roster) return;
    if (!people.length) return;
    const stillThere = people.some((p) => String(p.employee) === String(employeeId));
    if (!stillThere) setEmployeeId(String(people[0].employee));
  }, [people, employeeId, isManager, roster]);

  const tabs = isManager ? MANAGER_TABS : REP_TABS;
  // The patterns panel leads with 30 days for an AE and 7 for an SDR. It was
  // never told which, so every AE opened on a week holding one or two demos.
  const selectedTrack = (people.find((p) => String(p.employee) === String(employeeId)) || people[0])?.track
    || "junior";
  // "This week" gets the picker too — a manager's fastest way into any one
  // person's own profile, not just the worst call the queue already surfaces.
  // Picking someone there jumps straight to their scorecard (see
  // `selectPerson`); on the other three tabs it just swaps who's shown,
  // staying put.
  const showPersonPicker = ["queue", "scorecard", "growth", "patterns", "accounts"].includes(tab);
  const loading = !me || !calls || (isManager && !roster);

  const selectPerson = (id) => {
    setEmployeeId(id);
    if (tab === "queue") {
      setTrackFilter("");
      setTab("scorecard");
    }
  };

  return (
    <section className="Screen-Stack" style={{ padding: "26px 30px" }}>
      <section className="Page-Heading" style={{ marginBottom: 6 }}>
        <div>
          <span>Sales</span>
          <h1>RevOps</h1>
        </div>
      </section>
      <p style={{ margin: "0 0 16px", ...muted, fontSize: 14 }}>
        {isManager
          ? "Your team's calls, scored against the rubric they're actually on. Start with this "
            + "week's reviews, then open anybody to see how they're moving."
          : "Your calls, scored against the rubric for your track. Every score carries the words "
            + "you said and the moment you said them, so you can check it."}
      </p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, borderBottom: "1px solid var(--c-200)",
                    flexWrap: "wrap" }}>
        {tabs.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            style={{
              display: "flex", alignItems: "center", gap: 6, padding: "8px 4px", marginBottom: -1,
              background: "transparent", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
              color: tab === key ? "var(--blue-600)" : "var(--c-500)",
              borderBottom: tab === key ? "2px solid var(--blue-600)" : "2px solid transparent",
            }}
          >
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {loading && <p style={muted}>Loading…</p>}

      {!loading && isManager && showPersonPicker && people.length > 0 && (
        <ManagerBar roster={people} employeeId={employeeId} onSelect={selectPerson}
                    trackFilter={trackFilter} onTrackFilter={setTrackFilter} />
      )}

      {!loading && isManager && showPersonPicker && people.length === 0 && (
        <div style={card}>
          <p style={{ ...muted, fontSize: 13, margin: 0 }}>
            Nobody is on a RevOps track yet{trackFilter ? " in this group" : ""}. People appear here
            once they have an onboarding row, which is also what gives them a rubric — without one
            the scorer correctly refuses to score their calls.
          </p>
        </div>
      )}

      {!loading && (
        <>
          {tab === "queue" && isManager && (
            <ReviewQueue onOpenEmployee={(row) => {
              setEmployeeId(String(row.employee_id));
              setTrackFilter("");
              // A queue row names one call and one moment; open exactly that,
              // not the person's newest call.
              if (row.call_id) openCall(row.call_id, row.focus?.at_seconds ?? null);
              else setTab("scorecard");
            }} />
          )}
          {tab === "scorecard" && (
            <Scorecard rubric={rubric} me={me} calls={calls} people={people}
                       employeeId={employeeId} onSelectEmployee={setEmployeeId}
                       focusCall={focusCall} onFocusHandled={() => setFocusCall(null)}
                       onOpenAccount={openAccount} onCallsChanged={refreshCalls} />
          )}
          {tab === "growth" && (
            <GrowthPanel employeeId={employeeId} people={isManager ? [] : people}
                         onSelectEmployee={setEmployeeId} onOpenCall={openCall} />
          )}
          {tab === "patterns" && (
            <>
              <PatternsPanel employeeId={employeeId} people={isManager ? [] : people}
                             onSelectEmployee={setEmployeeId} isManager={isManager}
                             track={selectedTrack} onOpenCall={openCall}
                             playbookLinks={rubric?.playbook_links || {}} />
              <SkillProgress employeeId={employeeId} isManager={isManager} />
            </>
          )}
          {tab === "accounts" && (
            <AccountsPanel employeeId={isManager ? employeeId : null} isManager={isManager}
                           onOpenCall={openCall} focusCompany={focusCompany}
                           onFocusHandled={() => setFocusCompany(null)} />
          )}
          {tab === "team" && isManager && <TeamHeatmap />}
          {tab === "deals" && isManager && <DealsTab company={appData?.me?.company} />}
          {/* Deliberately NOT filtered to the selected person for a manager.
              This tab is about deals, not about a seller, and the person picker
              is not shown on it — so scoping it to whoever happened to be
              selected on another tab produced an empty panel with no visible
              control explaining why. A manager sees the team's deals and each
              card names its rep; a rep's own view is scoped by the server. */}
          {tab === "signals" && <DealPanel />}
          {tab === "hacks" && <HacksTab />}
          {tab === "how" && (
            <HowScoringWorks rubric={rubric} ownTrack={isManager ? null : people[0]?.track} />
          )}
        </>
      )}
    </section>
  );
}
